import React from "react";
import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import type { FlowStep, StepRun } from "../flow/types";
import { PHASES, stepById } from "../flow/flowGraph";
import StepCard from "./StepCard";
import type { RunnerState } from "../flow/engine/useFlowRunner";

interface Props {
  state: RunnerState;
  onRetry: () => void;
  onSkip: () => void;
  onRestart: () => void;
  onViewOrder?: () => void;
}

const emptyRun = (stepId: string): StepRun => ({ stepId, status: "pending", apiCalls: [], eventsProcessed: 0 });

const FlowTimeline: React.FC<Props> = ({ state, onRetry, onSkip, onRestart, onViewOrder }) => {
  const planned = state.plannedStepIds.map((id) => stepById(id)).filter(Boolean) as FlowStep[];
  const branches = state.branchStepIds.map((id) => stepById(id)).filter(Boolean) as FlowStep[];
  const total = planned.length + branches.length;

  const grouped = PHASES.map((phase) => ({
    phase,
    steps: planned.filter((s) => phase.stepIds.includes(s.id)),
  })).filter((g) => g.steps.length);

  let index = 0;
  return (
    <VStack align="stretch" spacing={4}>
      {grouped.map(({ phase, steps }) => {
        const doneCount = steps.filter((s) => ["completed", "skipped"].includes(state.runs[s.id]?.status)).length;
        return (
          <Box key={phase.id}>
            <HStack mb={2} pl="32px" justify="space-between">
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                {phase.label}
              </Text>
              <Text fontSize="10px" color="gray.400" fontFamily="mono">
                {doneCount}/{steps.length}
              </Text>
            </HStack>
            {steps.map((step) => {
              const i = index++;
              const isFailed = state.failedStepId === step.id;
              return (
                <StepCard
                  key={step.id}
                  step={step}
                  run={state.runs[step.id] || emptyRun(step.id)}
                  index={i}
                  isTarget={state.targetStepId === step.id}
                  isLast={i === total - 1}
                  onRetry={isFailed ? onRetry : undefined}
                  onSkip={isFailed ? onSkip : undefined}
                  onRestart={isFailed ? onRestart : undefined}
                  onViewOrder={isFailed ? onViewOrder : undefined}
                />
              );
            })}
          </Box>
        );
      })}
      {branches.length > 0 && (
        <Box>
          <Text mb={2} pl="32px" fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
            Branch actions
          </Text>
          {branches.map((step) => {
            const i = index++;
            const isFailed = state.failedStepId === step.id;
            return (
              <StepCard
                key={step.id}
                step={step}
                run={state.runs[step.id] || emptyRun(step.id)}
                index={i}
                isTarget={false}
                isLast={i === total - 1}
                isBranch
                onRetry={isFailed ? onRetry : undefined}
                onRestart={isFailed ? onRestart : undefined}
                onViewOrder={isFailed ? onViewOrder : undefined}
              />
            );
          })}
        </Box>
      )}
    </VStack>
  );
};

export default FlowTimeline;
